import React from "react"
import {formatTextTestIds} from "@next/utils"
import TileCardBrandName from "../../../components/tileCardBrandName"
import ThumbsSwiperCarousel from "../../../components/swiperColourwayImages/components/ThumbsSwiperCarousel"
import {BASELINE_PRODUCT_ID, SuitType, ProductType, SofaType, BundleType} from "../../../config/constants"
import ColourwayTitle from "../../../components/colourwaytitle"
import SwiperColourwayImages from "../../../components/swiperColourwayImages"
import ColourChipsComponent from "../../../components/colourchips"
import Price from "../../../components/price"
import BundlePrice from "../../../components/bundlePrice"
import BundleTitle from "../../../components/bundleTitle"
import SuitPrice from "../../../components/suitPrice"
import SuitTitle from "../../../components/suitTitle"
import ProductFavourites from "../../../components/favourites"
import {handleProductClick} from "../../../events"
import {TileCard, ImageContainer, TileCardBrandNameWrapper, StyledTitleWrapper, StyledLink} from "../../components"
import {TileCardContent} from "./components"
import useCarousel from "../../../hooks/useCarousel"
import {ColourwayData} from "../../../models/searchApi"
import {SuitColourwayData} from "../../../models/searchApi/suit"
import {SofaColourwayData} from "../../../models/searchApi/sofa"

export interface ProductProps {
    itemNumber: string
    firstItemNumber: string
    favouriteBtnEnabled: boolean
    productType: typeof SuitType | typeof ProductType | typeof SofaType | typeof BundleType
    enableBrandDisplay: boolean
    brand: string
    colourwaysHasSale: boolean
    linkUrl: string
    title: string
    price: string
    colour?: string
    currencyCode: string
    department: string
    tooltipTitle: string
    overallStarRating: number
    ratingText: string
    client: string
    textAlignment: string
    colourways: ColourwayData[] | SuitColourwayData[] | SofaColourwayData[]
}

export const formatLinkAriaLabel = (tooltipTitle, overallStarRating, ratingText) => {
    return overallStarRating > 0 ? `${tooltipTitle} | ${overallStarRating} ${ratingText}` : tooltipTitle
}

export const StandardTemplate = ({
    itemNumber,
    firstItemNumber,
    productType,
    enableBrandDisplay,
    brand,
    linkUrl,
    title,
    price,
    colour,
    currencyCode,
    department,
    tooltipTitle,
    overallStarRating,
    ratingText,
    client,
    textAlignment,
    colourways,
}: ProductProps) => {
    const {emblaRef, slideIndex, emblaApi, thumbsEmblaRef, thumbsEmblaApi, thumbsSlideIndex} = useCarousel({
        textAlignment,
        colourways,
    })

    return (
        <TileCard square elevation={0} data-testid={formatTextTestIds(`${productType}_summary_tile_${itemNumber}`)}>
            <ImageContainer>
                <SwiperColourwayImages
                    emblaRef={emblaRef}
                    emblaApi={emblaApi}
                    thumbsEmblaRef={thumbsEmblaRef}
                    thumbsEmblaApi={thumbsEmblaApi}
                />
                <ProductFavourites />
            </ImageContainer>
            <TileCardContent data-testid={formatTextTestIds(`${productType}_tile_card_content_${itemNumber}`)}>
                <ThumbsSwiperCarousel
                    thumbsEmblaRef={thumbsEmblaRef}
                    thumbsSlideIndex={thumbsSlideIndex}
                    slideIndex={slideIndex}
                />
                <StyledLink
                    href={linkUrl}
                    data-testid={formatTextTestIds(`${productType}_summary_tile_${itemNumber}_link`)}
                    style={{textDecoration: "none"}}
                    onClick={() => {
                        handleProductClick({
                            id: itemNumber,
                            title,
                            price,
                            colour,
                            currencyCode,
                            department,
                            firstItemNumber,
                            brand,
                            client,
                        })
                    }}
                    aria-label={formatLinkAriaLabel(tooltipTitle, overallStarRating, ratingText)}
                >
                    {enableBrandDisplay && brand !== null && (
                        <TileCardBrandNameWrapper
                            data-testid={formatTextTestIds(`${productType}_summary_tile_${itemNumber}_brand_wrapper`)}
                        >
                            <TileCardBrandName
                                data-testid={formatTextTestIds(`${productType}_summary_tile_${itemNumber}_brand_name`)}
                            >
                                {brand}
                            </TileCardBrandName>
                        </TileCardBrandNameWrapper>
                    )}
                    {(productType === ProductType ||
                        productType === SofaType ||
                        itemNumber === BASELINE_PRODUCT_ID) && (
                        <>
                            <StyledTitleWrapper>
                                <ColourwayTitle />
                            </StyledTitleWrapper>
                            <Price />
                        </>
                    )}
                    {(productType === SuitType || itemNumber === BASELINE_PRODUCT_ID) && (
                        <>
                            <StyledTitleWrapper>
                                <SuitTitle />
                            </StyledTitleWrapper>
                            <SuitPrice />
                        </>
                    )}
                    {(productType === BundleType || itemNumber === BASELINE_PRODUCT_ID) && (
                        <>
                            <StyledTitleWrapper>
                                <BundleTitle />
                            </StyledTitleWrapper>
                            <BundlePrice />
                        </>
                    )}
                </StyledLink>
                <ColourChipsComponent slideIndex={slideIndex} />
            </TileCardContent>
        </TileCard>
    )
}

export default StandardTemplate
